import { JSONSchema3or4 } from 'to-json-schema';

import { SolveWidgetProduct } from './types';
import {
  ConversationChannel,
  CustomEventCustomizationSettings,
  SetContextVariableCustomizationSettings,
} from './workflowBuilderAPITypes';
import { ActionBuilderTemplate } from 'src/utils/constants';
import {
  ActionBuilderActionTypes,
  APITemplateTypes,
  ContextVariableTypes,
} from 'src/utils/enums';

export interface Action {
  action_description: string;
  action_fields: ActionFields;
  action_id: string;
  action_name: string;
  action_type: `${ActionBuilderActionTypes}`;
  automated_object_id?: string;
  icon_url?: string;
  is_background_task?: boolean;
  is_deleted?: boolean;
  is_imported?: boolean;
  is_recommended?: boolean;
  modified_date?: string;
}

const handoffActions: Readonly<Action['action_type'][]> = [
  ActionBuilderActionTypes.BRAND_EMBASSY_HANDOFF,
  ActionBuilderActionTypes.CATCH_ALL_HANDOFF,
  ActionBuilderActionTypes.FRESHCHAT_CHAT_HANDOFF,
  ActionBuilderActionTypes.FRESHDESK_TICKET_CREATION,
  ActionBuilderActionTypes.FRESHDESK_TICKET_AND_CONTACT_CREATION,
  ActionBuilderActionTypes.GORGIAS_CHAT_HANDOFF,
  ActionBuilderActionTypes.GORGIAS_TICKET_CREATION,
  ActionBuilderActionTypes.PYLON_TICKET_CREATION,
  ActionBuilderActionTypes.TALKDESK_CHAT_HANDOFF,
  ActionBuilderActionTypes.INTERCOM_HANDOFF,
  ActionBuilderActionTypes.JIRA_SERVICE_MANAGEMENT_CREATE_REQUEST,
  ActionBuilderActionTypes.KUSTOMER_HANDOFF,
  ActionBuilderActionTypes.KUSTOMER_CHAT_HANDOFF,
  ActionBuilderActionTypes.KUSTOMER_CONVERSATION_CREATION,
  ActionBuilderActionTypes.LIVE_CHAT_CHAT_HANDOFF,
  ActionBuilderActionTypes.SALESFORCE_CASE_CREATION,
  ActionBuilderActionTypes.SALESFORCE_CHAT_HANDOFF,
  ActionBuilderActionTypes.SALESFORCE_HANDOFF,
  ActionBuilderActionTypes.SALESFORCE_MESSAGING_HANDOFF,
  ActionBuilderActionTypes.SNAPENGAGE_CHAT_HANDOFF,
  ActionBuilderActionTypes.ZENDESK_CHAT_HANDOFF,
  ActionBuilderActionTypes.ZENDESK_CHAT_HANDOFF_V2,
  ActionBuilderActionTypes.ZENDESK_HANDOFF,
  ActionBuilderActionTypes.ZENDESK_MESSAGING_HANDOFF,
  ActionBuilderActionTypes.ZENDESK_TICKET_CREATION,
  ActionBuilderActionTypes.FRONT_TICKET_CREATION,
  ActionBuilderActionTypes.HALOCRM_TICKET_CREATION,
  ActionBuilderActionTypes.ZAMMAD_TICKET_CREATION,
  ActionBuilderActionTypes.FORETHOUGHT_LIVE_CHAT_HANDOFF,
  ActionBuilderActionTypes.SUNCO_LIVE_CHAT,
  ActionBuilderActionTypes.END_INTERACTIVE_EMAIL_CHAT,
  ActionBuilderActionTypes.ZENDESK_TALK_HANDOFF,
  ActionBuilderActionTypes.ZENDESK_SIP_CALL,
  ActionBuilderActionTypes.GLADLY_CHAT_HANDOFF,
  ActionBuilderActionTypes.GRANULAR_JIRA_SERVICE_MANAGEMENT_CREATE_REQUEST,
  ActionBuilderActionTypes.HUBSPOT_TICKET_CREATION,
  ActionBuilderActionTypes.HUBSPOT_HELPDESK_TICKET_CREATION,
  ActionBuilderActionTypes.HUBSPOT_CHAT_HANDOFF,
  ActionBuilderActionTypes.DYNAMICS_365_CASE_CREATION,
  ActionBuilderActionTypes.COMM_100_CHAT_HANDOFF,
  ActionBuilderActionTypes.CONTACTME_CHAT_HANDOFF,
  ActionBuilderActionTypes.FRONT_CHAT_HANDOFF,
  ActionBuilderActionTypes.QUIQ_CHAT_HANDOFF,
  ActionBuilderActionTypes.CALLCORP_CHAT_HANDOFF,
  ActionBuilderActionTypes.SERVICENOW_CASE_CREATION,
  ActionBuilderActionTypes.SERVICENOW_CASE_INCIDENT_CREATION,
] as const;

export function isHandoffActionType(
  actionType: Action['action_type'],
): boolean {
  return handoffActions.includes(actionType);
}

export function isApiActionType(actionType: Action['action_type']): boolean {
  return actionType === ActionBuilderActionTypes.API;
}

export function isZendeskActionType(
  actionType: Action['action_type'],
): boolean {
  return actionType === ActionBuilderActionTypes.ZENDESK_FLOWSTATE_ACTION;
}

export function isOtherActionType(actionType: Action['action_type']): boolean {
  return (
    !isHandoffActionType(actionType) &&
    !isApiActionType(actionType) &&
    !isZendeskActionType(actionType)
  );
}

export type ActionPanelMode = CustomizableActions | 'hidden';

export type CustomizableActions =
  | ActionBuilderActionTypes.ARTICLE_SUGGESTION
  | ActionBuilderActionTypes.INTERCOM_HANDOFF
  | ActionBuilderActionTypes.SALESFORCE_HANDOFF
  | ActionBuilderActionTypes.SALESFORCE_CASE_CREATION
  | ActionBuilderActionTypes.SALESFORCE_CHAT_HANDOFF
  | ActionBuilderActionTypes.ZENDESK_HANDOFF
  | ActionBuilderActionTypes.CATCH_ALL_HANDOFF
  | ActionBuilderActionTypes.JIRA_SERVICE_MANAGEMENT_CREATE_REQUEST
  | ActionBuilderActionTypes.ZENDESK_CHAT_HANDOFF
  | ActionBuilderActionTypes.ZENDESK_CHAT_HANDOFF_V2
  | ActionBuilderActionTypes.ZENDESK_TICKET_CREATION
  | ActionBuilderActionTypes.FRONT_TICKET_CREATION
  | ActionBuilderActionTypes.HALOCRM_TICKET_CREATION
  | ActionBuilderActionTypes.ZAMMAD_TICKET_CREATION
  | ActionBuilderActionTypes.HYPERLINK_REDIRECT
  | ActionBuilderActionTypes.KUSTOMER_HANDOFF
  | ActionBuilderActionTypes.KUSTOMER_CHAT_HANDOFF
  | ActionBuilderActionTypes.KUSTOMER_CONVERSATION_CREATION
  | ActionBuilderActionTypes.TRIGGER_EVENT
  | ActionBuilderActionTypes.FRESHDESK_TICKET_CREATION
  | ActionBuilderActionTypes.ARTICLE_SUGGESTION_GRANULAR
  | ActionBuilderActionTypes.SNAPENGAGE_CHAT_HANDOFF
  | ActionBuilderActionTypes.GRANULAR_ARTICLE_SUGGESTION_V2
  | ActionBuilderActionTypes.GORGIAS_CHAT_HANDOFF
  | ActionBuilderActionTypes.GORGIAS_TICKET_CREATION
  | ActionBuilderActionTypes.PYLON_TICKET_CREATION
  | ActionBuilderActionTypes.TALKDESK_CHAT_HANDOFF
  | ActionBuilderActionTypes.FRESHCHAT_CHAT_HANDOFF
  | ActionBuilderActionTypes.ZENDESK_MESSAGING_HANDOFF
  | ActionBuilderActionTypes.SALESFORCE_MESSAGING_HANDOFF
  | ActionBuilderActionTypes.CONTEXT_VARIABLE_MAPPING
  | ActionBuilderActionTypes.ZENDESK_ATTACHMENT_UPLOAD
  | ActionBuilderActionTypes.LIVE_CHAT_CHAT_HANDOFF
  | ActionBuilderActionTypes.PARSE_JWT
  | ActionBuilderActionTypes.SET_CONTEXT_VARIABLE
  | ActionBuilderActionTypes.FORETHOUGHT_LIVE_CHAT_HANDOFF
  | ActionBuilderActionTypes.SUNCO_LIVE_CHAT
  | ActionBuilderActionTypes.DYNAMIC_CARD
  | ActionBuilderActionTypes.PRODUCT_RECOMMENDATION
  | ActionBuilderActionTypes.FRESHDESK_TICKET_AND_CONTACT_CREATION
  | ActionBuilderActionTypes.INTERCOM_TICKET_CREATION
  | ActionBuilderActionTypes.INTERCOM_LIVE_CHAT
  | ActionBuilderActionTypes.CONVERSATION_OAUTH_SIGN_IN
  | ActionBuilderActionTypes.ZENDESK_TALK_HANDOFF
  | ActionBuilderActionTypes.ZENDESK_SIP_CALL
  | ActionBuilderActionTypes.GLADLY_CHAT_HANDOFF
  | ActionBuilderActionTypes.CUSTOM_EVENT
  | ActionBuilderActionTypes.GRANULAR_JIRA_SERVICE_MANAGEMENT_CREATE_REQUEST
  | ActionBuilderActionTypes.HUBSPOT_TICKET_CREATION
  | ActionBuilderActionTypes.HUBSPOT_HELPDESK_TICKET_CREATION
  | ActionBuilderActionTypes.HUBSPOT_CHAT_HANDOFF
  | ActionBuilderActionTypes.DYNAMICS_365_CASE_CREATION
  | ActionBuilderActionTypes.COMM_100_CHAT_HANDOFF
  | ActionBuilderActionTypes.QUIQ_CHAT_HANDOFF
  | ActionBuilderActionTypes.CALLCORP_CHAT_HANDOFF
  | ActionBuilderActionTypes.SERVICENOW_CASE_CREATION
  | ActionBuilderActionTypes.SERVICENOW_CASE_INCIDENT_CREATION;

export type ActionFields =
  | ApiActionFields
  | ShopifyActionTemplateFields
  | SnowflakeDataQueryTemplateFields
  | ActionBuilderAPIActionFields
  | SetContextVariableCustomizationSettings
  | TemplateAPIActionFields
  | CustomEventCustomizationSettings
  | McpActionFields
  | ZendeskActionFields
  | BrowserAgentActionFields;

export interface ShopifyActionTemplateFields {
  customer_email: string;
  customer_id: string;
  output_variable?: string;
  password: string;
  url: string;
  username: string;
}

export interface SnowflakeDataQueryTemplateFields {
  account: string;
  data_table_name: string;
  db_name: string;
  output_fields?: {
    kv_list: { key: string; value: string }[];
  };
  password: string;
  query_condition?: string;
  username: string;
}

export interface ShopifySelectedActionTemplate extends Omit<
  Action,
  'action_fields' | 'action_id'
> {
  action_fields: ShopifyActionTemplateFields;
  action_id?: string;
  action_type: ActionBuilderActionTypes.SHOPIFY_ORDER_STATUS_TEMPLATE;
}

export interface ApiActionSelectedActionTemplate {
  action_description: string;
  action_name: string;
  action_type: APITemplateTypes;
  icon: string;
}

export type SelectedActionTemplate =
  | ShopifySelectedActionTemplate
  | ApiActionSelectedActionTemplate;

export interface ActionBuilderAction {
  actions: Action[];
}

export interface ActionBuilderActionRequest {
  product?: SolveWidgetProduct;
  shouldReturnAll?: boolean;
}

export interface SelectedAction extends Omit<
  Action,
  'action_fields' | 'action_id'
> {
  action_fields: ActionBuilderReduxActionFields;
  action_id?: string;
}

export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export interface ActionBuilderAPIActionFields {
  authorization_config_id?: string | null;
  authorization_conversation_oauth_client_id?: string | null;
  authorization_integration?: string | null;
  authorization_type?: string | null;
  body_parameters?: string | null;
  connector_id?: string | null;
  headers?: {
    [key: string]: string;
  } | null;
  jmespath_query?: string | null;
  method: string;
  natural_language_query?: string | null;
  oauth_connector_id?: string | null;
  oauth_scope?: string | null;
  output_context_variables?: Record<string, string>;
  query_parameters?: {
    [key: string]: string;
  } | null;
  url: string;
}

export interface TemplateAPIActionFields extends ActionBuilderAPIActionFields {
  mock_responses: Record<
    string,
    { response: Record<string, unknown>; status_code: number }
  >;
}

export interface McpActionFields {
  connector_id: string;
  connector_name?: string;
  json_schema_str: string;
  org_id: number;
}

export type ZendeskActionKind = 'zendesk' | 'external' | 'custom' | 'workflow';

export interface ZendeskActionFields {
  input_schema: Record<string, unknown>;
  output_schema: Record<string, unknown>;
  version_id: string | null;
  view_url?: string | null;
  zendesk_action_id: string;
  zendesk_kind: ZendeskActionKind;
}

export interface BrowserAgentSecret {
  password: string;
  totp_secret: string;
  url: string;
  username: string;
}

export type BrowserAgentOutputContextVariables =
  | Record<string, string>
  | {
      kv_list?: { key: string; value: string }[];
    };

export interface BrowserAgentActionFields {
  domains?: string[];
  initial_url?: string;
  loading_texts?: string[];
  output_context_variables?: BrowserAgentOutputContextVariables;
  policy: string;
  secrets?: BrowserAgentSecret[];
  user_agent?: string | null;
}

export interface UpdateActionSecrets {
  actionId: string;
  secrets: BrowserAgentSecret[];
}

export function areActionBuilderFields(
  actionFields: ActionFields,
): actionFields is ActionBuilderAPIActionFields {
  return 'output_context_variables' in actionFields;
}

export function isTemplateApiActionFields(
  actionFields: ActionFields | Record<string, never> = {},
): actionFields is TemplateAPIActionFields {
  return 'mock_responses' in actionFields;
}

export function isMcpActionFields(
  actionFields: ActionFields | Record<string, never> = {},
): actionFields is McpActionFields {
  return (
    'json_schema_str' in actionFields &&
    'connector_id' in actionFields &&
    'org_id' in actionFields
  );
}

export function isZendeskActionFields(
  actionFields: ActionFields | Record<string, never> = {},
): actionFields is ZendeskActionFields {
  return 'zendesk_action_id' in actionFields && 'zendesk_kind' in actionFields;
}

export function isBrowserAgentActionFields(
  actionFields: ActionFields,
): actionFields is BrowserAgentActionFields {
  return 'policy' in actionFields;
}

export function isApiActionFields(
  fields: ActionFields,
): fields is ApiActionFields {
  return 'url' in fields && 'method' in fields;
}

export function areShopifyFields(
  actionFields: ActionFields,
): actionFields is ShopifyActionTemplateFields {
  return 'customer_email' in actionFields && 'customer_id' in actionFields;
}

export function areSnowflakeFields(
  actionFields: ActionFields,
): actionFields is SnowflakeDataQueryTemplateFields {
  return 'query_condition' in actionFields;
}

export interface KeyValuePairsDict {
  name: string;
  value: string;
}

export interface ActionBuilderReduxActionFields extends Omit<
  ActionBuilderAPIActionFields,
  'query_parameters' | 'headers'
> {
  headers: KeyValuePairsDict[];
  query_parameters: KeyValuePairsDict[];
}

export interface AuthorizationConfig {
  authorization_fields: Record<string, string>;
  authorization_id: string;
  authorization_type: string;
}

export interface SettingsInterface {
  authorization_configs: AuthorizationConfig[];
  available_workflow_tags?: Array<string>;
  context_variables: ContextVariable[];
  template_context_variables?: ContextVariable[];
  usable_system_context_variables?: ContextVariable[];
}

/**
 * A single, uncollapsed reference to a context variable from a workflow/intent
 * source. Structurally identical to {@link ActionReference} (both satisfy
 * `WorkflowReferenceLike`), so the shared `actionReferences` utils apply directly.
 * Preserves the raw `source_id` (with its `:draft` / `:live` / `:saved:{version}`
 * suffix) and product so the dashboard can deeplink to the exact channel/version,
 * unlike `context_variable_usage_by_entity_id`, which collapses versions.
 */
export interface ContextVariableReference {
  intent_definition_id: string | null;
  parent_autoflow_id: string | null;
  product: string | null;
  source_entity_type: string | null;
  source_id: string;
  source_type: string | null;
}

export interface ContextVariableUsagesResponse {
  context_variable_usage_by_entity_id: Record<
    string,
    {
      context_variable_id: string;
      entity_id: string;
      entity_type: EntityType;
    }[]
  >;
  context_variable_usage_by_entity_type: ContextVariableUsageByEntityType;
  /**
   * Per-reference detail keyed by context_variable_id. Optional for backward
   * compatibility with backends/caches that predate the enriched response.
   */
  context_variable_id_to_references?: Record<
    string,
    ContextVariableReference[]
  >;
  list_cvs_values_usage_in_expression: ListContextVariableUsageByValues;
}

export interface ContextVariableUsageByEntityType {
  [contextVariableId: string]: {
    context_variable_id: string;
    usage_by_entity_type: UsageByEntityType;
  };
}

export interface ListContextVariableUsageByValues {
  [contextVariableId: string]: string[];
}

export type EntityType =
  | 'action'
  | 'email_configuration'
  | 'email_workflow'
  | 'ticket_field_mapping'
  | 'widget_workflow'
  | 'autoflow_workflow'
  | 'api_workflow'
  | 'email_automation'
  | 'interactive_email_workflow'
  | 'playbook_workflow'
  | 'slack_workflow'
  | 'voice_workflow';

export type UsageByEntityType = {
  [key in EntityType]: boolean;
};

interface ListOption {
  /** explanatory text shown to end users beneath the option */
  description?: string;
  label: string;
  value: string;
}

export interface DynamicListOption {
  // id will only exist if this represents a primitive type and can be used as a cv
  context_variable_id: string | null;
  context_variable_name: string;
  // options will only exist if this is a complex type, it will not have an id and cannot be used as a cv
  options: DynamicListOption[] | null;
}

export interface DynamicListConfig {
  context_variables?: DynamicListOption[];
  json_schema: Record<string, unknown>;
}

export interface DynamicOptionsConfig {
  enabled: boolean;
}

export interface SubcategoryConfig {
  options_by_parent_value: Record<string, ListOption[]>;
  parent_context_variable_id: string;
}

export interface ContextVariableConfigurationTypes {
  dynamic_list_config?: DynamicListConfig | null;
  dynamic_options_config?: DynamicOptionsConfig | null;
  is_alphanumeric?: boolean;
  max_length?: number;
  min_length?: number;
  subcategory_config?: SubcategoryConfig | null;
}

export interface ContextVariable {
  configuration_fields?: ContextVariableConfigurationTypes;
  context_variable_id: string;
  context_variable_name: string;
  context_variable_type: keyof typeof ContextVariableTypes;
  created_date?: string;
  default_value?: unknown;
  disabled?: boolean;
  is_private_context_variable: boolean;
  is_system_context_variable?: boolean;
  is_universal_context_variable: boolean;
  is_workflow_context?: boolean;
  list_type_options?: ListOption[];
}

export type CreateContextVariableRequest = Omit<
  ContextVariable,
  | 'context_variable_id'
  | 'created_date'
  | 'is_private_context_variable'
  | 'is_universal_context_variable'
>;

export interface AddOrUpdateActionInterface {
  action_description: string;
  action_fields: ActionFields;
  action_name: string;
  action_type: string;
  is_background_task?: boolean;
  product?: SolveWidgetProduct;
}

export interface ActionUsageReference {
  intent_definition_id: string | null;
  parent_autoflow_id: string | null;
  product: string | null;
  /** Raw source id with its ':draft' / ':live' / ':saved:{version}' suffix. */
  source_id: string;
  source_entity_type: string | null;
  source_type: string | null;
}

export interface ActionUpdateImpactResponse {
  added_input_context_variables: string[];
  added_output_context_variables: string[];
  /** False when the org flag is off or the action is not an API action. */
  enabled: boolean;
  /**
   * The edit changes the action's context-variable surface in a way that can
   * invalidate referencing workflows (added input or removed output).
   */
  is_breaking: boolean;
  /**
   * Every agent reference to this action. With reference resolution on,
   * every execution resolves the action at load time, so all of them adopt
   * a republished edit immediately — there is no draft/live/saved adoption
   * tiering. Optional only for deploy skew with backends that predate the
   * flat list.
   */
  references?: ActionUsageReference[];
  removed_input_context_variables: string[];
  removed_output_context_variables: string[];
}

/**
 * One agent (workflow) that blocks an API action republish, from the 409
 * payload of PUT workflow-builder/action/{action_id}. `source_id` is the
 * clean (unsuffixed) workflow id for the Quick view deeplink.
 */
export interface RepublishBlockedAgent {
  errors: string[];
  intent_definition_id: string | null;
  name: string;
  parent_autoflow_id: string | null;
  product: string | null;
  source_id: string;
  source_type: string | null;
}

export interface OutputParameterKeysType {
  [key: string]: string | null;
}

export interface AdditionalDetailsModalActionData {
  action_description?: string;
  action_name: string;
}

export interface ActiveIntegrationsInterface {
  integrations: ActiveIntegration[];
}

/**
 * A single, uncollapsed reference to an action from a workflow/intent source.
 *
 * Preserves the raw `source_id` (including the `:draft` / `:live` /
 * `:saved:{version}` suffix), the product/channel, and source metadata so the
 * dashboard can deeplink to the exact channel / version / state where the action
 * is referenced — unlike `action_id_to_intent_ids`, which only exposes the
 * deduplicated parent workflow IDs.
 */
export interface ActionReference {
  intent_definition_id: string | null;
  parent_autoflow_id: string | null;
  product: string | null;
  source_entity_type: string | null;
  source_id: string;
  source_type: string | null;
}

export interface ActionUsagesResponse {
  action_id_to_intent_ids: Record<string, string[]>;
  /**
   * Per-reference detail keyed by action ID. Optional for backward compatibility
   * with backends that have not yet been deployed with the enriched response.
   */
  action_id_to_references?: Record<string, ActionReference[]>;
  oauth_client_id_to_action_ids: Record<string, string[]>;
  oauth_client_id_to_intent_ids: Record<string, string[]>;
}
export interface ActiveIntegration {
  connector_display_name: string;
  connector_id: string;
  emblem_url: string;
  integration: string;
}

export interface ProxyTestRequest {
  authorization_integration?: string | null;
  authorization_type?: string | null;
  body?: unknown;
  connector_id?: string | null;
  conversation_oauth_client_id?: string | null;
  headers?: object;
  http_method: string;
  oauth_connector_id?: string | null;
  oauth_scope?: string | null;
  query_parameters?: object;
  url: string;
}

export interface ProxyTestResponse {
  body: object;
  status_code: number;
}

export interface BackgroundProxyResponse {
  channel_id: string;
  execution_id: string;
}

export interface BackgroundProxyResultResponse {
  body: unknown;
  error: string | null;
  execution_id: string;
  status: 'pending' | 'success' | 'error';
  status_code: number | null;
}

export interface KVPairs {
  key: string;
  type?: 'string' | 'number' | 'boolean' | 'object' | 'array';
  value: string | undefined;
}

export interface DynamicListKVPairs extends KVPairs {
  jsonSchema: JSONSchema3or4;
  newContextVariableName: string;
  /** Set when the user can't write context variables; the CV upsert in save is skipped. */
  readOnlyDynamicList?: true;
  /**
   * Which dynamic list type to create for this output. Only meaningful while
   * the CV does not exist yet -- an existing CV keeps the type it was created
   * with. Defaults to single-select when absent, so actions saved before
   * multi-select existed keep their behaviour.
   */
  newContextVariableType?: 'DYNAMIC_LIST' | 'DYNAMIC_LIST_MULTI_SELECT';
}

export type OutputValues = KVPairs | DynamicListKVPairs;

export type AuthorizationType =
  | 'bearer'
  | 'integration'
  | 'authorization_client'
  | 'oauth_client_credentials';
export type AuthorizationField = {
  connectorId?: string;
  id: string;
  scope?: string;
  type: AuthorizationType;
} | null;
export type ContentType = 'form-url' | 'json' | 'none';

export interface ApiActionFields {
  authorization: AuthorizationField;
  body: string | null;
  headers: KVPairs[];
  jmespathQuery: string;
  method: Method;
  params: KVPairs[];
  url: string;
}

export interface SelectedActionDetail {
  actionId?: string;
  description: string;
  fields: ApiActionFields | BrowserAgentActionFields;
  is_background_task?: boolean;
  loading_texts?: string[];
  name: string;
  outputValues: OutputValues[];
  tabId: string;
}

export interface ActionBuilderCollectionTemplates {
  templates: ActionBuilderTemplate[];
}

export interface ValidateJmesPathRequest {
  expression: string;
  test_data: unknown;
}

export interface ValidateJmesPathResponse {
  error: string | null;
  expression: string;
  result: unknown;
  test_data: unknown;
  valid: boolean;
}

export interface ConversationOauthClient {
  auth_url: string;
  authentication_type: 'client_secret_basic' | 'client_secret_post';
  client_id: string;
  client_secret: string;
  conversation_oauth_client_id: string;
  created_date: string;
  modified_date: string;
  name: string;
  org_id: number;
  scope?: string | null;
  token_url: string;
}

export interface CreateConversationOauthClientRequest {
  auth_url: string;
  client_id: string;
  client_secret: string;
  name: string;
  scope?: string | null;
  token_url: string;
}

export interface UpdateConversationOauthClientRequest extends CreateConversationOauthClientRequest {
  conversation_oauth_client_id: string;
}

export interface ConversationOauthCredentialResponse {
  auth_uri?: string;
  found: boolean;
}

/**
 * Maps workflow entity types to their corresponding channels.
 * Non-workflow entity types are kept but set to undefined for type exhaustiveness.
 */
export const workflowEntityTypeToChannel = {
  action: undefined,
  api_workflow: 'api',
  autoflow_workflow: 'widget',
  email_automation: undefined,
  email_configuration: undefined,
  email_workflow: 'email',
  interactive_email_workflow: 'email',
  playbook_workflow: 'playbook',
  slack_workflow: 'slack',
  ticket_field_mapping: undefined,
  voice_workflow: 'voice',
  widget_workflow: 'widget',
} as const satisfies Record<EntityType, ConversationChannel | undefined>;
